import React from 'react';
import { motion } from 'framer-motion';
import { Palette, Layers, Instagram, FileText, Package, Target } from 'lucide-react';
import { services } from '../data/mock';

const iconComponents = {
  Palette,
  Layers,
  Instagram,
  FileText,
  Package,
  Target,
};

const ServicesSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3,
      },
    },
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 40,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 80,
        damping: 15,
      },
    },
  };

  return (
    <section className="py-24 bg-dark-grey relative overflow-hidden">
      {/* Grain Texture Overlay */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" 
           style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"%3E%3Cfilter id="noise"%3E%3CfeTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" /%3E%3C/filter%3E%3Crect width="100%25" height="100%25" filter="url(%23noise)" /%3E%3C/svg%3E")' }} 
      />

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-gold/10 border border-gold/20 rounded-full">
            <span className="text-gold text-sm font-mono uppercase tracking-wider">Services</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Ne <span className="text-gold">Yapıyoruz?</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
            Markanızın dijital ve fiziksel tüm dokunuş noktalarında profesyonel tasarım çözümleri sunuyoruz.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {services.map((service) => {
            const IconComponent = iconComponents[service.icon];
            
            return (
              <motion.div
                key={service.id}
                variants={cardVariants}
                whileHover={{ y: -8 }}
                className="group relative bg-black/40 backdrop-blur-sm border border-white/5 rounded-2xl p-8 hover:border-gold/30 transition-all duration-500 overflow-hidden"
              >
                {/* Hover Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-gold/0 via-gold/0 to-gold/0 group-hover:from-gold/5 group-hover:via-gold/0 group-hover:to-transparent transition-all duration-500" />

                {/* Icon */}
                <div className="relative mb-6">
                  <div className="inline-flex p-4 bg-gold/10 rounded-2xl group-hover:bg-gold group-hover:scale-110 transition-all duration-500">
                    <IconComponent className="w-8 h-8 text-gold group-hover:text-black transition-colors duration-500" />
                  </div>
                </div>

                {/* Content */}
                <div className="relative">
                  <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-gold transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {service.description}
                  </p>
                </div>

                {/* Decorative Element */}
                <div className="absolute bottom-0 right-0 w-32 h-32 bg-gold/5 rounded-full blur-3xl group-hover:bg-gold/10 transition-all duration-500 -z-10" />
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-20"
        >
          <p className="text-gray-400 mb-6 text-lg">
            Projeniz için doğru çözümü birlikte bulalım
          </p>
          <button className="group inline-flex items-center gap-3 px-8 py-4 bg-gold text-black font-semibold rounded-full hover:bg-gold-light transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-gold/50">
            Ücretsiz Görüşme Talep Et
            <Target className="w-5 h-5 group-hover:rotate-45 transition-transform duration-300" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
